import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Image,
  Input,
  InputNumber,
  Row,
  Spin,
  Switch,
  Upload,
  Alert,
  Divider,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useSuccessErrorModal from "../../hooks/successErrorModal/useSuccessErrorModal";
import Endpoints from "../../api/endpoints";
import EndpointFunctions from "../../utils/Functions/endpointFunctions";
import PageRoutes from "../../utils/routes";

const UpdateCardBackgroundPreset = () => {
  const [form] = Form.useForm();
  const [preset, setPreset] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [imageFileList, setImageFileList] = useState([]);

  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { presetId } = useParams();
  const { showModal, closeModal, ModalComponent } = useSuccessErrorModal();

  useEffect(() => {
    fetchPreset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPreset = async () => {
    setIsLoading(true);
    const result = await EndpointFunctions.fetchData({
      axiosPrivate: axiosPrivate,
      endpointURL: Endpoints.getCardBackgroundPreset,
      httpParams: { id: presetId },
    });

    setIsLoading(false);

    if (result.success) {
      setPreset(result.data);
      form.setFieldsValue({
        name: result.data.name,
        displayOrder: result.data.displayOrder,
        isActive: result.data.isActive,
      });
    } else {
      showModal(result.errorTitle, result.errorMessage, () => closeModal());
    }
  };

  const onSaveMeta = async (values) => {
    setIsSaving(true);

    const requestBody = {
      name: values.name,
      displayOrder: values.displayOrder,
      isActive: values.isActive,
    };

    const result = await EndpointFunctions.putData({
      axiosPrivate: axiosPrivate,
      endpointURL: `${Endpoints.updateCardBackgroundPreset}/${presetId}`,
      data: requestBody,
      isMultiPart: false,
    });

    setIsSaving(false);

    if (result.success) {
      showModal("Başarılı", "Preset bilgileri güncellendi.", () => {
        closeModal();
        setPreset(result.data);
      });
    } else {
      showModal(result.errorTitle, result.errorMessage, () => closeModal());
    }
  };

  const onReplaceImage = async () => {
    if (!imageFileList[0]?.originFileObj) {
      showModal("Hata", "Lütfen yeni bir görsel seçin.", () => closeModal());
      return;
    }

    setIsReplacing(true);

    const formData = new FormData();
    formData.append("file", imageFileList[0].originFileObj);

    const result = await EndpointFunctions.putData({
      axiosPrivate: axiosPrivate,
      endpointURL: `${Endpoints.replaceCardBackgroundPresetImage}/${presetId}`,
      formData: formData,
      isMultiPart: true,
    });

    setIsReplacing(false);

    if (result.success) {
      showModal(
        "Başarılı",
        "Görsel değiştirildi. Bu preset'i seçmiş tüm işletmelerin kartında yeni görsel otomatik görünecek.",
        () => {
          closeModal();
          setPreset(result.data);
          setImageFileList([]);
        }
      );
    } else {
      showModal(result.errorTitle, result.errorMessage, () => closeModal());
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Spin spinning={isLoading}>
        <Card title={`Preset Düzenle (ID: ${presetId})`}>
          {preset && !preset.isActive && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Bu preset şu an pasif"
              description="Pasif preset shop picker listesinde gözükmez. Mevcut shop'lar (daha önce seçenler) kart görseli render edilmeye devam eder. 'Aktif' switch'ini açıp kaydederek tekrar listeye alabilirsin."
            />
          )}

          <Form form={form} layout="vertical" onFinish={onSaveMeta}>
            <Row gutter={[16, 0]}>
              <Col span={16}>
                <Form.Item
                  name="name"
                  label="İsim (admin için iç isim)"
                  rules={[
                    { required: true, message: "İsim zorunludur" },
                    { max: 100, message: "Max 100 karakter" },
                  ]}>
                  <Input />
                </Form.Item>
              </Col>

              <Col span={4}>
                <Form.Item name="displayOrder" label="Sıra">
                  <InputNumber min={0} max={32767} style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              <Col span={4}>
                <Form.Item
                  name="isActive"
                  label="Aktif"
                  valuePropName="checked">
                  <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isSaving}>
                Bilgileri Kaydet
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={() => navigate(PageRoutes.cardBackgroundPresets)}>
                Geri
              </Button>
            </Form.Item>
          </Form>

          <Divider />

          <h3 style={{ marginTop: 0 }}>Mevcut Görsel</h3>
          {preset?.backgroundImageUrl ? (
            <Image
              src={preset.backgroundImageUrl}
              alt={preset.name}
              style={{
                maxWidth: 240,
                borderRadius: 8,
                border: "1px solid #eee",
              }}
            />
          ) : (
            <p>Görsel yok</p>
          )}

          <Divider />

          <h3>Görseli Değiştir</h3>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Bu preset'i seçmiş tüm işletmelerin kartında yeni görsel otomatik görünecek."
            description="Hash bazlı dosya adı sayesinde Flutter image cache otomatik bypass olur, eski görsel ekranda kalmaz. Aynı kurallar geçerli: 350:502 oran, min 800px yükseklik, max 5MB."
          />
          <Upload
            beforeUpload={() => false}
            listType="picture"
            maxCount={1}
            onChange={(e) => setImageFileList(e.fileList)}
            accept="image/png, image/jpeg"
            fileList={imageFileList}>
            <Button icon={<UploadOutlined />}>Yeni Görsel Seç</Button>
          </Upload>
          <Button
            type="primary"
            danger
            style={{ marginTop: 16 }}
            onClick={onReplaceImage}
            loading={isReplacing}
            disabled={imageFileList.length === 0}>
            Görseli Değiştir
          </Button>
        </Card>
      </Spin>
      <ModalComponent />
    </div>
  );
};

export default UpdateCardBackgroundPreset;
